import React, { useState } from "react";
import * as S from "./CreateReceiptStyle";
import { receiptService } from "../../Services/receiptService";
import { FaSearch } from "react-icons/fa";
import SearchModal from "./SearchModal";

export default function CreateReceipt({ onClose, onSuccess }) {
  const [type, setType] = useState("contract"); // 'contract' or 'withdraw'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Estado para controlar qual modal de busca está aberto
  // null | 'client' | 'contract' | 'withdraw'
  const [searchModalOpen, setSearchModalOpen] = useState(null);

  const [formData, setFormData] = useState({
    clientId: "",
    clientNameDisplay: "", // Apenas visual
    contractId: "",
    withdrawId: "",
    description: "",
    file: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({ ...prev, file: e.target.files[0] }));
    }
  };

  // Funções chamadas ao selecionar um item no modal
  const handleSelectClient = (client) => {
    setFormData((prev) => ({
      ...prev,
      clientId: client.id,
      clientNameDisplay: client.name,
    }));
    setSearchModalOpen(null);
  };

  const handleSelectContract = (contract) => {
    setFormData((prev) => ({
      ...prev,
      contractId: contract.id,
      clientId: contract.clientId || prev.clientId, // Se vier no objeto, já preenche
      clientNameDisplay: contract.clientName || prev.clientNameDisplay,
    }));
    setSearchModalOpen(null);
  };

  const handleSelectWithdraw = (withdraw) => {
    setFormData((prev) => ({
      ...prev,
      withdrawId: withdraw.id,
      contractId: withdraw.contractId || prev.contractId,
      clientId: withdraw.clientId || prev.clientId,
      clientNameDisplay: withdraw.clientName || prev.clientNameDisplay,
    }));
    setSearchModalOpen(null);
  };

  const handleSubmit = async () => {
    setError("");

    // Validação básica (removi contractId da obrigação geral)
    if (!formData.clientId || !formData.description || !formData.file) {
      setError("Preencha Cliente, Descrição e Arquivo.");
      return;
    }

    // Validação específica por tipo
    if (type === "contract" && !formData.contractId) {
      setError("ID do Contrato é obrigatório para recibo de contrato.");
      return;
    }

    if (type === "withdraw" && !formData.withdrawId) {
      setError("ID do Saque é obrigatório para este tipo.");
      return;
    }

    setLoading(true);
    const data = new FormData();
    data.append("clientId", formData.clientId);

    // Lógica para descrição automática
    let finalDescription = formData.description;

    if (type === "withdraw") {
      data.append("withdrawId", formData.withdrawId);

      // Se não tem contrato, assume indicação e ajusta descrição
      if (!formData.contractId) {
        finalDescription = `${formData.description} - SAQUE DE INDICAÇÃO`;
        data.append("contractId", 0); // Envia 0 para o backend não quebrar se esperar int
      } else {
        data.append("contractId", formData.contractId);
      }
    } else {
      // Tipo contrato sempre tem contractId
      data.append("contractId", formData.contractId);
    }

    data.append("description", finalDescription);
    data.append("file", formData.file);

    try {
      if (type === "withdraw") {
        await receiptService.createWithdrawReceipt(data);
      } else {
        await receiptService.createContractReceipt(data);
      }
      alert("Recibo criado com sucesso!");
      onSuccess();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Erro ao criar recibo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Renderiza o modal de busca se estiver ativo */}
      {searchModalOpen && (
        <SearchModal
          type={searchModalOpen}
          onClose={() => setSearchModalOpen(null)}
          onSelect={
            searchModalOpen === "client"
              ? handleSelectClient
              : searchModalOpen === "contract"
              ? handleSelectContract
              : handleSelectWithdraw
          }
        />
      )}

      <S.Overlay>
        <S.FormContainer>
          <S.Title>Novo Recibo</S.Title>

          <S.ToggleGroup>
            <S.ToggleOption
              active={type === "contract"}
              onClick={() => setType("contract")}
            >
              CONTRATO
            </S.ToggleOption>
            <S.ToggleOption
              active={type === "withdraw"}
              onClick={() => setType("withdraw")}
            >
              SAQUE
            </S.ToggleOption>
          </S.ToggleGroup>

          {error && (
            <p style={{ color: "#e74c3c", fontSize: 14, textAlign: "center" }}>
              {error}
            </p>
          )}

          {/* Campo de ID Cliente com botão de busca */}
          <S.FormGroup>
            <label>
              ID do Cliente *{" "}
              {formData.clientNameDisplay && `- ${formData.clientNameDisplay}`}
            </label>
            <S.InputWithIcon>
              <input
                type="number"
                name="clientId"
                value={formData.clientId}
                onChange={handleChange}
                placeholder="Ex: 123"
              />
              <button
                onClick={() => setSearchModalOpen("client")}
                tabIndex="-1"
              >
                <FaSearch />
              </button>
            </S.InputWithIcon>
          </S.FormGroup>

          {/* Campo de ID Contrato com botão de busca */}
          <S.FormGroup>
            <label>
              ID do Contrato {type === "withdraw" ? "(Opcional)" : "*"}
            </label>
            <S.InputWithIcon>
              <input
                type="number"
                name="contractId"
                value={formData.contractId}
                onChange={handleChange}
                placeholder="Ex: 55"
              />
              <button
                onClick={() => setSearchModalOpen("contract")}
                tabIndex="-1"
              >
                <FaSearch />
              </button>
            </S.InputWithIcon>
          </S.FormGroup>

          {/* Campo de ID Saque (só aparece se for tipo SAQUE) */}
          {type === "withdraw" && (
            <S.FormGroup>
              <label>ID do Saque *</label>
              <S.InputWithIcon>
                <input
                  type="number"
                  name="withdrawId"
                  value={formData.withdrawId}
                  onChange={handleChange}
                  placeholder="Ex: 88"
                />
                <button
                  onClick={() => setSearchModalOpen("withdraw")}
                  tabIndex="-1"
                >
                  <FaSearch />
                </button>
              </S.InputWithIcon>
            </S.FormGroup>
          )}

          <S.FormGroup>
            <label>Descrição *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Detalhes do comprovante..."
            />
          </S.FormGroup>

          <S.FormGroup>
            <label>Arquivo (Imagem ou PDF) *</label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              style={{ padding: 5 }}
            />
          </S.FormGroup>

          <S.ButtonGroup>
            <S.CancelButton onClick={onClose}>Cancelar</S.CancelButton>
            <S.SubmitButton onClick={handleSubmit} disabled={loading}>
              {loading ? "Enviando..." : "Criar Recibo"}
            </S.SubmitButton>
          </S.ButtonGroup>
        </S.FormContainer>
      </S.Overlay>
    </>
  );
}
